import { Color } from 'modules/styles/colors';
import { GlobalSearchItem } from 'store/global/types';
import { AppName, CompanyName, getAppName } from 'modules/utils/ConfigUtils';

export interface HelpPhoneItem {
  title: string;
  subtitle?: string;
  phone: string;
}

export const HELP_CARDS: Record<string, HelpPhoneItem> = {
  TechnicalHelp: {
    title: 'Technical Support',
    phone: '(800) 442-5502'
  },
  Billing: {
    title: 'Have a question about a bill?',
    subtitle:
      'Call the Intermountain Billing Helpline 8AM - 6PM, Monday through Friday (except holidays)',
    phone: '(866) 415-6556'
  },
  Covid19: {
    title: 'Emotional Health Relief Hotline',
    subtitle: 'Supportive resources, tools, and treatments ',
    phone: '(833) 442-2211'
  }
};

export interface ContactInfoItem {
  title: string;
  subtitle: string;
  phone: string;
  icon: string;
}

export interface Card {
  title: string;
  schedule: string;
  week: string;
}
export interface EmotionalSupport {
  title: string;
  phone: string;
  url: string;
  description: string[];
  card: Card;
  getMoreInfo: string;
}

export const EMOTIONAL_SUPPORT: EmotionalSupport = {
  url: 'https://intermountainhealthcare.org/services/behavioral-health/',
  phone: '(833) 442-2211',
  title: 'Behavioral Health Services Navigation Line',
  description: [
    `Navigating Behavioral Health doesn’t have to be complicated. Intermountain’s Behavioral Health Services Navigation Line will help you find the right resources at the right time to meet your needs for mental health and substance use disorders treatment.`,
    `${CompanyName} continues to lead the industry in integrating treatment of behavioral and mental health conditions with traditional approaches to care. Our experienced and caring team of psychologists, physicians, and behavioral and mental health specialists work closely with patients of every age group and socioeconomic background. Whether you are suffering from anorexia, anxiety, autism, or other disorder we will partner with you in understanding your unique needs, and building an individual treatment plan utilizing innovative technology and our advanced facilities.`
  ],
  card: {
    title: 'Caregivers Available',
    schedule: ' 7:00 a.m. – 7:00 p.m.',
    week: '7 Days a Week'
  },
  getMoreInfo: 'Get more info'
};

export const CONTACT_INFO: Record<string, ContactInfoItem> = {
  TechSupport: {
    title: 'Technical Support',
    subtitle: `Mon-Fri 7AM - 8PM MT ${'\n'}Saturday 9AM - 2PM MT`,
    phone: '(800) 442-5502',
    icon: 'Phone'
  },
  Billing: {
    title: 'Billing',
    subtitle: 'Mon-Fri 8AM - 6PM MT',
    phone: '(866) 415-6556',
    icon: 'Phone'
  }
};

export interface HelpLink {
  title: string;
  icon: string;
  mobilePath: string;
  webPath: string;
}

export const HELP_SCREEN_LINKS: Record<string, HelpLink> = {
  Feedback: {
    title: 'Share Feedback',
    icon: 'ChevronRight',
    mobilePath: 'ShareFeedback',
    webPath: '/u/help-support/share-feedback'
  },
  TechSupport: {
    title: 'Technical Support',
    icon: 'ChevronRight',
    mobilePath: 'TechnicalSupport',
    webPath: '/u/help-support/technical-support'
  },
  Contact: {
    title: 'Contact Us',
    icon: 'ChevronRight',
    mobilePath: 'ContactUs',
    webPath: '/u/help-support/contact'
  },
  About: {
    title: 'About This App',
    icon: 'ChevronRight',
    mobilePath: 'AboutUs',
    webPath: '/u/help-support/about'
  },
  MyHealthPlusStatusPage: {
    title: `${getAppName(AppName.base, '', AppName.suffix1)} Status Page`,
    icon: 'ChevronRight',
    mobilePath: 'MyHealthPlusStatusPage',
    webPath: '/status'
  }
};

export const searchFormattedHelp: GlobalSearchItem[] = [
  {
    title: 'Share Feedback',
    keywords: ['feedback', 'comment', 'contact'],
    navWebPath: HELP_SCREEN_LINKS.Feedback.webPath,
    navParams: {},
    icon: 'InfoIcon',
    iconProps: {
      set: 'material',
      name: 'help_outline',
      color: Color.primary
    },
    category: 'Help'
  },
  {
    title: 'Technical Support',
    keywords: ['technical', 'support', 'help'],
    navWebPath: HELP_SCREEN_LINKS.TechSupport.webPath,
    navParams: {},
    icon: 'InfoIcon',
    iconProps: {
      set: 'material',
      name: 'help_outline',
      color: Color.primary
    },
    category: 'Help'
  },
  {
    title: 'Contact Us',
    keywords: ['contact', 'help'],
    navWebPath: HELP_SCREEN_LINKS.Contact.webPath,
    navParams: {},
    icon: 'InfoIcon',
    iconProps: {
      set: 'material',
      name: 'help_outline',
      color: Color.primary
    },
    category: 'Help'
  }
];

export interface QualtricsQuestion {
  questionId: string;
  type: string;
  display: string;
  options: object;
}

export interface QualtricsResult {
  sessionId: string;
  questions: Array<QualtricsQuestion>;
  embeddedData: object;
  responses: object;
  done: boolean;
}

export interface QualtricsMeta {
  requestId: string;
  httpStatus: string;
}

export interface QualtricsSession {
  meta: QualtricsMeta;
  result: QualtricsResult;
}

export const SHARE_FEEDBACK_SCENARIOS = {
  appointmentScheduling: 'Appointment Scheduling',
  connectCare: 'Connect Care',
  eVisit: 'E-Visit',
  financialAssistance: 'Financial Assistance',
  proxyRequestForm: 'Proxy Request Form',
  shareFeedback: 'Share Feedback',
  symptomChecker: 'Symptom Checker'
};

export type ShareFeedbackScenarios = typeof SHARE_FEEDBACK_SCENARIOS | string;

export interface ShareFeedbackLocationState {
  scenario: ShareFeedbackScenarios;
  screenLocation: string;
  navigateTo?: string;
  navigateToParams?: any;
}

export interface FeedbackFormValues {
  feedback: string;
  showContact: boolean;
  name: string;
  email: string;
  preferredPhone: string;
  scenario: ShareFeedbackScenarios;
  screenLocation: string;
}

export const DATA_CENTER_ID = 'sjc1';

export const SURVEY_ID = 'SV_cHeAXZCUMM7qJQ9';

export const SESSION_URL = `https://${DATA_CENTER_ID}.qualtrics.com/API/v3/surveys/${SURVEY_ID}/sessions`;

export const STATUS_PAGE_URL = 'https://mhpstatus.intermountainhealthcare.org/#';

export const PRIVACY_NOTICES_URL =
  'https://intermountainhealthcare.org/website-information/privacy-notices/';
